import { IUser } from "@clickster/constant-definitions/dist-esm/entities/user/types";
import { IQueryProps } from "../interfaces/general/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserService } from "../services/users";
import { removeKeysFromObject } from "../utils/objects";
import { IPermission } from "@clickster/constant-definitions/dist-esm/entities/user/types/permission";
import { PermissionsService } from "../services/permissions";


export const useGetUsers = ({ params, handleSuccess, handleError, staleTime, customKey, enabled }: IQueryProps<IUser>) => {
  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery(customKey ? [customKey] : ['users'], () => UserService.list(params), {
    onError: (error) => {
      console.log({ error });
    },
    onSuccess: handleSuccess ? handleSuccess : undefined,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime,
    enabled
  });

  return {
    users: (data ?? []) as IUser[],
    isLoading,
    isFetching,
    refetch,
  };
};

interface IUseGetUserById extends IQueryProps<IUser> {
  id: string;
}

export const useGetUsersById = ({ id, enabled, handleSuccess, staleTime }: IUseGetUserById) => {
  const {
    data = {},
    isLoading,
    isFetching,
    refetch,
  } = useQuery(['userById', id], () => UserService.find(id), {
    onError: (error) => {
      console.log({ error });
    },
    onSuccess: handleSuccess ? handleSuccess : undefined,
    enabled: enabled && !!id,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime,
  });

  return {
    user: removeKeysFromObject((data ?? {}) as IUser, ['__v', 'deleted']),
    isLoading,
    isFetching,
    refetch,
  };
};

export const useGetUsersMe = ({ enabled, handleSuccess, handleError, staleTime }: IQueryProps<IUser>) => {
  const {
    data = {},
    isLoading,
    isFetching,
    refetch,
  } = useQuery(['userMe'], () => UserService.getUserMe(), {
    onError: handleError ? handleError : undefined,
    onSuccess: handleSuccess ? handleSuccess : undefined,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime,
    enabled,
  });

  return {
    user: removeKeysFromObject((data ?? {}) as IUser, ['__v', 'deleted']),
    isLoading,
    isFetching,
    refetch,
  };
};

export const useUpdateUsersById = () => {
  const queryClient = useQueryClient();
  const { refetch: refetchUserMe } = useGetUsersMe({ enabled: false });
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation(
    (user: IUser) => UserService.update(user),
    {
      onSuccess: async (data, variables) => {
        // Invalidate all user-related queries
        queryClient.invalidateQueries(['userMe']);
        queryClient.invalidateQueries(['users']);
        queryClient.invalidateQueries(['userById', variables._id]);

        // Refetch user data to update global state and wait for it to complete
        const result = await refetchUserMe();

        // Update the store with the refetched user data to ensure isAdmin flag is recalculated
        if (result.data) {
          const { setUserStore } = await import('@/src/stores/user/actions');
          setUserStore(result.data);
        }
      },
      onError: (error) => console.log({ error }),
    }
  );

  return {
    updateUserById: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
}

export const useCreateUser = () => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation((user: IUser) => UserService.create(user), {
    onError: (error) => console.log({ error }),
  });

  return {
    createUser: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
}


export const useGetPermissions = ({ params, handleSuccess, handleError }: IQueryProps<IPermission>) => {
  const {
    data = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery(['permissions'], () => PermissionsService.list(params), {
    onError: (error) => {
      console.log({ error });
    },
    onSuccess: handleSuccess ? handleSuccess : undefined,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    permissions: (data ?? []) as IPermission[],
    isLoading,
    isFetching,
    refetch,
  };
};

/**
 * Hook to toggle a campaign in user's favorites and automatically update the user data
 * @returns Object containing mutation function and loading states
 */
export const useToggleFavoriteCampaign = () => {
  const { refetch: refetchUser } = useGetUsersMe({ enabled: false });
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation(
    ({userId, campaignId, add}: {userId: string, campaignId: string, add: boolean}) => 
      UserService.toggleFavoriteCampaign(userId, campaignId, add),
    {
      onSuccess: () => {
        // Refetch user data to update global state
        refetchUser();
      },
      onError: (error) => console.error('Error toggling favorite campaign:', error),
    }
  );

  return {
    toggleFavoriteCampaign: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

/**
 * Hook to remove a campaign from user's favorites and automatically update the user data
 * @returns Object containing mutation function and loading states
 */
export const useRemoveFavoriteCampaign = () => {
  const { refetch: refetchUser } = useGetUsersMe({ enabled: false });
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation(
    ({userId, campaignId}: {userId: string, campaignId: string}) => 
      UserService.removeFavoriteCampaign(userId, campaignId),
    {
      onSuccess: () => {
        // Refetch user data to update global state
        refetchUser();
      },
      onError: (error) => console.error('Error removing favorite campaign:', error),
    }
  );

  return {
    removeFavoriteCampaign: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

/**
 * Hook to delete users (logical deletion)
 * @returns Object containing mutation function and loading states
 */
export const useDeleteUsers = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation(
    (ids: string[]) => UserService.remove(ids),
    {
      onSuccess: () => {
        // Invalidate all user-related queries to refetch data
        queryClient.invalidateQueries(['users']);
        queryClient.invalidateQueries(['userById']);
      },
      onError: (error) => console.error('Error deleting users:', error),
    }
  );

  return {
    deleteUsers: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};